import { getMaxRealEstateValue } from "@/component/Mortgage/Calculator/domain";
import type { TargetPropertyAcceptableType } from "@/component/Mortgage/Calculator/types";

// AAA
export const calculateCSOKMonthlyInstallmentFirstYear = (loanAmount: number): number => (loanAmount * 0.03) / 12;

// BBB
export const calculateCSOKMonthlyInstallmentSecondYear = (maturityMonths: number, loanAmount: number): number =>
	loanAmount * (0.03 / 12 / (1 - 1 / (1 + 0.03 / 12) ** (maturityMonths - 12)));

// CCC
export const calculateMarketLoanMonthlyInstallment = (maturityMonths: number, loanAmount: number): number =>
	loanAmount * (0.075 / 12 / (1 - 1 / (1 + 0.075 / 12) ** maturityMonths));

// DDD
export const calculateCSOKLoanTotal = (maturityMonths: number, loanAmount: number): number =>
	calculateCSOKMonthlyInstallmentFirstYear(loanAmount) * 12 +
	calculateCSOKMonthlyInstallmentSecondYear(maturityMonths, loanAmount) * (maturityMonths - 12);

// EEE
export const calculateMarketLoanTotal = (maturityMonths: number, loanAmount: number): number =>
	calculateMarketLoanMonthlyInstallment(maturityMonths, loanAmount) * maturityMonths;

// FFF
export const calculateTotalDifference = (maturityMonths: number, loanAmount: number): number =>
	calculateMarketLoanTotal(maturityMonths, loanAmount) - calculateCSOKLoanTotal(maturityMonths, loanAmount);

export const calculateJTMPercentage = (monthlyIncome: number): number => {
	if (monthlyIncome < 600_000) {
		return 0.5;
	}

	return 0.6;
};

export const calculateCSOKAmount = (currentChildren: number, plannedChildren: number): number => {
	if (plannedChildren > 0 && plannedChildren + currentChildren === 1) {
		return 15_000_000;
	}

	if (plannedChildren > 0 && plannedChildren + currentChildren === 2) {
		return 30_000_000;
	}

	if (plannedChildren > 0 && plannedChildren + currentChildren >= 3) {
		return 50_000_000;
	}
};

interface GetKoTextParams {
	currentChildren: number;
	monthlyIncome: number;
	plannedChildren: number;
	loanAmount: number;
	realEstateValue: number;
	isMarried: boolean;
	wantsFirstHouse: boolean;
	maturityYears: number;
	qualifiesForHigherLtv: boolean;
	targetPropertyAcceptableType: TargetPropertyAcceptableType;
}

export const getKoText = ({
	currentChildren,
	monthlyIncome,
	plannedChildren,
	loanAmount,
	realEstateValue,
	isMarried,
	wantsFirstHouse,
	maturityYears,
	qualifiesForHigherLtv,
	targetPropertyAcceptableType,
}: GetKoTextParams): string => {
	if (!isMarried) {
		return "Kizárólag házaspárok igényelhetik a támogatást.";
	}

	if (!plannedChildren) {
		return "Csak vállalt gyermek mellett igényelhető a támogatás.";
	}

	const maxRealEstateValue = getMaxRealEstateValue({
		wantsFirstHouse,
		targetPropertyAcceptableType,
		onlyCsokPlus: true,
	});

	if (realEstateValue > maxRealEstateValue) {
		return "Az ingatlan értéke túl magas a CSOK Plusz igényléséhez.";
	}

	if (
		calculateJTMPercentage(monthlyIncome) * monthlyIncome <
		calculateCSOKMonthlyInstallmentSecondYear(maturityYears * 12, loanAmount)
	) {
		return "JTM-KO";
	}

	const ltv = qualifiesForHigherLtv ? 0.9 : 0.8;
	const maxLoanAmount = realEstateValue * ltv;

	if (loanAmount > maxLoanAmount) {
		return `A hitelösszeg nem haladhatja meg az ingatlan értékének ${ltv * 100}%-át.`;
	}

	if (calculateCSOKAmount(currentChildren, plannedChildren) < loanAmount) {
		return "A gyermekek száma alapján ekkora összeg nem igényelhető.";
	}

	return undefined;
};

export function isConsultantUsedCalculator(consultant: string, source: string): boolean {
	return (
		(window.location.href.includes("source") && window.location.href.includes("consultant")) ||
		(!!consultant && !!source)
	);
}
